import { FC, useEffect, useRef, useState } from "react";
import useSubscribersOnlyOption from "components/Pages/Home/Helpers/useSubOnlyNotification.tsx";
import Icon from "components/Elements/Icon/Icon";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useCustomController } from "../useCustomController.js";
import { setBodyValue } from "components/Pages/Home/UploadForms/formsFields.js";
import { Toggle } from "components/Elements/Toggle/Toggle.js";
import useMediaQueries from "hooks/useMediaQueries.js";

import "./reusableFields.scss";

interface IDownloadLimitProps {
  textKey: string;
  icon: string;
  subcribersOnly: boolean;
  getValues: () => any;
  tooltip?: string;
}

const DownloadLimit: FC<IDownloadLimitProps> = (props) => {
  const { fieldControls } = useCustomController(props);
  const { mobileCondition } = useMediaQueries();
  const [enableLimit, setEnableLimit] = useState(!!fieldControls.value);
  const [limitDownloads, setLimitDownloads] = useState(
    fieldControls.value || 0
  );
  const { getTextByKey } = useContent();
  const interval = useRef(null);

  useEffect(() => {
    if (!enableLimit) {
      setLimitDownloads(0);
    } else {
      if (limitDownloads === 0) {
        setLimitDownloads(1);
      }
    }
  }, [enableLimit]);

  useEffect(() => {
    fieldControls.onChange(limitDownloads);
    setBodyValue(props, limitDownloads);
  }, [limitDownloads]);

  const handleMouseDownIncrease = () => {
    interval.current = setInterval(() => {
      setEnableLimit(true);
      setLimitDownloads((prev) => prev + 1);
    }, 100);
  };

  const handleMouseUpIncrease = () => {
    clearInterval(interval.current);
  };

  const handleMouseDownDecrease = () => {
    interval.current = setInterval(() => {
      setLimitDownloads((prev) => {
        prev === 1 && setEnableLimit(false);
        return prev ? prev - 1 : 0;
      });
    }, 100);
  };

  const handleMouseUpDecrease = () => {
    clearInterval(interval.current);
  };

  const increaseNumber = () => {
    setLimitDownloads((current) => {
      if (current === 0) {
        setEnableLimit(true);
      }
      return current + 1;
    });
  };

  const decreaseNumber = () => {
    setLimitDownloads((current) => {
      if (current === 1) {
        setEnableLimit(false);
      }
      return current === 0 ? current : current - 1;
    });
  };

  const handleChange = (e: Event) => {
    const value = +e.target.value;

    if (!isNaN(value) && value >= 0) {
      setLimitDownloads(value || 0);
      if (value === 0) {
        setEnableLimit(false);
      }
    } else {
      setLimitDownloads(0);
      setEnableLimit(false);
    }
  };

  const { SubOnlyNotification, handleSetStatus } = useSubscribersOnlyOption({
    callbackFunc: setEnableLimit,
    subcribersOnly: props.subcribersOnly,
    titleKey: "downloads-limit-text",
  });

  return (
    <>
      <div className="security-option column">
        <div className="row">
          {props.icon ? (
            <Icon name={props.icon} gray iconClass="security-option-icon" />
          ) : null}
          <div className="security-downloads-limit-text">
            {getTextByKey("downloads-limit-text")}
          </div>
          <div className="flex-spacer" />
          <SubOnlyNotification />
          <Toggle status={enableLimit} setStatus={handleSetStatus} />
        </div>
        {enableLimit &&
        ((mobileCondition && limitDownloads) || !mobileCondition) ? (
          <div className="downloads-limit-number ">
            <div
              className={
                limitDownloads
                  ? "decrease-number"
                  : "decrease-number decrease-number-disabled"
              }
              onClick={decreaseNumber}
              onMouseDown={handleMouseDownDecrease}
              onMouseUp={handleMouseUpDecrease}
            >
              -
            </div>
            <input
              type="number"
              min={0}
              name="downloads-limit"
              value={limitDownloads}
              onChange={handleChange}
            />
            <div
              className="increase-number"
              onClick={increaseNumber}
              onMouseDown={handleMouseDownIncrease}
              onMouseUp={handleMouseUpIncrease}
            >
              +
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default DownloadLimit;
